import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Tooltip, IconButton, TextField } from '@mui/material';

import { Vehicle } from '../../../../types/vehicle';

import { Icon } from '@liftango/ui-icons';
interface VehicleInfoProps {
  vehicle: Vehicle;
  draft: { standardSeats: number; wheelchairSeats: number };
  onEditVehicle: (vehicle: Vehicle) => void;
  onSeatChange: (vehicle: Vehicle, field: 'standardSeats' | 'wheelchairSeats', value: number) => void;
  onSeatBlurOrEnter: (vehicle: Vehicle) => void;
}

const VehicleInfo: React.FC<VehicleInfoProps> = ({
  vehicle,
  draft,
  onEditVehicle,
  onSeatChange,
  onSeatBlurOrEnter,
}) => {
  const { t } = useTranslation('mapWizards');

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      sx={{
        bgcolor: '#fafafa',
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
        lineHeight: 1.1,
      }}
    >
      {/* Top row: edit + name */}
      <Box display="flex" alignItems="center" gap={0.25} sx={{ minWidth: 0 }}>
        <Tooltip title={t('fleetOperations.vehicleInfo.editVehicle')}>
          <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onEditVehicle(vehicle)}>
            <Icon size={16} name="pen-to-square" />
          </IconButton>
        </Tooltip>
        <Typography
          variant="body2"
          sx={{
            minWidth: 0,
            flex: '1 1 auto',
            fontWeight: 500,
            fontSize: '0.85rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={vehicle.name}
        >
          {vehicle.name}
        </Typography>
      </Box>

      {/* Bottom row: passenger capacity */}
      <Box display="flex" alignItems="center" gap={0.5} sx={{ pl: 0.25 }}>
        <Icon size={14} name="user" style={{ color: '#222' }} />
        <TextField
          type="number"
          variant="standard"
          value={draft.standardSeats}
          onChange={e => {
            let val = Number(e.target.value);
            if (isNaN(val) || val < 0) val = 0;
            onSeatChange(vehicle, 'standardSeats', val);
          }}
          onBlur={() => onSeatBlurOrEnter(vehicle)}
          onKeyDown={e => { if (e.key === 'Enter') onSeatBlurOrEnter(vehicle); }}
          inputProps={{ min: 0, style: { width: 28, padding: 0, fontSize: 12, textAlign: 'center' } }}
          sx={{ width: 32 }}
        />
        <Icon size={14} name="wheelchair-move" style={{ color: '#1976d2', marginLeft: 4 }} />
        <TextField
          type="number"
          variant="standard"
          value={draft.wheelchairSeats}
          onChange={e => {
            let val = Number(e.target.value);
            if (isNaN(val) || val < 0) val = 0;
            onSeatChange(vehicle, 'wheelchairSeats', val);
          }}
          onBlur={() => onSeatBlurOrEnter(vehicle)}
          onKeyDown={e => { if (e.key === 'Enter') onSeatBlurOrEnter(vehicle); }}
          inputProps={{ min: 0, style: { width: 28, padding: 0, fontSize: 12, textAlign: 'center' } }}
          sx={{ width: 32 }}
        />
      </Box>
    </Box>
  );
};

export default VehicleInfo;
